import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { CreatePage, FuseInput, FuseSelect } from '/src/components';
import { useCompanyStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { CreateCompanyForm, DataModelType, FormErrors, Region, SocketLineOfBusiness } from '/src/lib/types';

/**
 * Create Company Page
 */
export const CreateCompanyPage = observer(() => {
  /** Context **/
  const companyStore = useCompanyStore();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<CreateCompanyForm>({
    name: '',
    longName: '',
    street: '',
    city: '',
    zip: '',
    contactPerson: '',
    phonePrimary: '',
    email: '',
    website: 'https://',
    companyType: '', // TODO: create CompanyType enum
    isLender: false,
    notes: [],
    files: [],
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    const errorMessages = {
      name: 'Company Name is required',
      socketLineOfBusiness: 'Line of Business is required',
      companyType: 'Company Type is required',
      'address.street': 'Street is required',
      'address.city': 'City is required',
      'address.state': 'State is required',
      'address.zip': 'Zip code is required',
      'phone.primary': 'Primary phone number is required',
      contactPerson: 'Contact person is required',
    };

    if (!formValues?.['name']) errors['name'] = errorMessages['name'];
    if (!formValues?.['socketLineOfBusiness']) errors['socketLineOfBusiness'] = errorMessages['socketLineOfBusiness'];
    if (!formValues?.['companyType']) errors['companyType'] = errorMessages['companyType'];
    if (!formValues?.street) errors['address.street'] = errorMessages['address.street'];
    if (!formValues?.city) errors['address.city'] = errorMessages['address.city'];
    if (!formValues?.state) errors['address.state'] = errorMessages['address.state'];
    if (!formValues?.zip) errors['address.zip'] = errorMessages['address.zip'];
    if (!formValues?.phonePrimary) errors['phone.primary'] = errorMessages['phone.primary'];
    if (!formValues?.['contactPerson']) errors['contactPerson'] = errorMessages['contactPerson'];

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Creates a Company.
   *
   * @remarks
   * Redirects to the new Company's Detail page afterwards.
   */
  const create = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await companyStore.create(formValues);
      if (!response) throw new Error(`Could not create Company with data: ${JSON.stringify(formValues)}`);

      toast.success(toastMessages[response.isLender ? 'createLender' : 'createCompany'].success);
      navigate(`/${response.isLender ? 'investors' : 'companies'}/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages[formValues.isLender ? 'createLender' : 'createCompany'].error);
    }
  };

  /** Render **/
  return (
    <CreatePage modelType={DataModelType.Company} onCreate={create}>
      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Company Short Name */}
        <FuseInput
          label="Company Short Name"
          value={formValues?.name}
          updateValue={updateFormValue('name')}
          maxLength={40}
          required
          error={formErrors.name}
        />

        {/* Company Long Name */}
        <FuseInput
          label="Company Long Name"
          value={formValues?.longName}
          updateValue={updateFormValue('longName')}
          maxLength={40}
          error={formErrors.longName}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Socket Line of Business */}
        <FuseSelect
          label="Line of Business"
          options={Object.values(SocketLineOfBusiness)}
          value={formValues?.socketLineOfBusiness}
          updateValue={updateFormValue('socketLineOfBusiness')}
          placeholder="Line of Business"
          // placeholder="Select a Line of Business for this Company"
          required // TODO: is this required or not?
          error={formErrors.socketLineOfBusiness}
        />

        {/* Company Type */}
        <FuseInput
          label="Company Type"
          value={formValues?.companyType}
          updateValue={updateFormValue('companyType')}
          maxLength={40}
          required
          error={formErrors.companyType}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        {/* Street */}
        <FuseInput
          label="Street"
          value={formValues?.street}
          updateValue={updateFormValue('street')}
          maxLength={40}
          required
          error={formErrors['address.street']}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* City */}
        <Box display="flex" flex={60}>
          <FuseInput
            label="City"
            value={formValues?.city}
            updateValue={updateFormValue('city')}
            maxLength={40}
            required
            error={formErrors['address.city']}
          />
        </Box>

        {/* State */}
        <Box display="flex" flex={15}>
          <FuseSelect
            label="State"
            value={formValues?.state}
            options={Object.values(Region) as string[]}
            updateValue={updateFormValue('state')}
            placeholder="State"
            required
            error={formErrors['address.state']}
          />
        </Box>

        {/* Zip */}
        <Box display="flex" flex={25}>
          <FuseInput
            label="Zip"
            value={formValues?.zip}
            updateValue={updateFormValue('zip')}
            maxLength={5}
            type="number"
            required
            error={formErrors['address.zip']}
          />
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* PRIMARY PHONE */}
        <FuseInput
          label="Primary Phone"
          value={formValues?.phonePrimary}
          updateValue={updateFormValue('phonePrimary')}
          type="phone"
          required
          error={formErrors['phone.primary']}
        />

        {/* SECONDARY PHONE */}
        <FuseInput
          label="Secondary Phone"
          value={formValues?.phoneSecondary}
          updateValue={updateFormValue('phoneSecondary')}
          type="phone"
          error={formErrors['phone.secondary']}
        />

        {/* FAX */}
        <FuseInput
          label="Fax"
          value={formValues?.phoneFax}
          updateValue={updateFormValue('phoneFax')}
          type="phone"
          error={formErrors['phone.fax']}
        />
      </Box>

      {/* CONTACT PERSON */}
      <FuseInput
        label="Contact Person"
        value={formValues?.contactPerson}
        updateValue={updateFormValue('contactPerson')}
        maxLength={40}
        required
        error={formErrors.contactPerson}
      />

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* EMAIL */}
        <FuseInput
          label="Email"
          value={formValues?.email}
          updateValue={updateFormValue('email')}
          type="email"
          maxLength={40}
          boxProps={{ className: 'pad-bottom-xs' }}
          error={formErrors.email}
        />

        {/* WEBSITE */}
        <FuseInput
          label="Website"
          value={formValues?.website}
          updateValue={updateFormValue('website')}
          type="url"
          maxLength={40}
          error={formErrors.website}
        />
      </Box>
    </CreatePage>
  );
});
